import { z } from 'zod'

const MARKER_RE = /⟦VIEW⟧([\s\S]*?)⟦\/VIEW⟧/

// site_id and camera_id are OPTIONAL in the wire schema: the assistant
// may omit them when the current page_context already fixes them
// (CDV-014). planViewIntent fills them in before dispatch and rejects
// the intent if they are still missing afterwards.
const PhotosSchema = z.object({
  kind: z.literal('photos'),
  site_id: z.string().min(1).optional(),
  // date required only when event_ids is absent; the planner enforces this.
  date: z.string().min(1).optional(),
  camera_id: z.string().optional(),
  // CDV-005: when the LLM has already identified specific events worth
  // showing (via get_recent_events / analysis tools), pass their ids and
  // the sala renders only those — date / camera_id are ignored server-side.
  event_ids: z.array(z.number().int()).optional(),
})

const AnalysesSchema = z.object({
  kind: z.literal('analyses'),
  site_id: z.string().min(1).optional(),
  date: z.string().optional(),
  date_from: z.string().optional(),
  date_to: z.string().optional(),
  camera_id: z.string().optional(),
  analysis_type: z.string().optional(),
})

const TimelineSchema = z.object({
  kind: z.literal('timeline'),
  site_id: z.string().min(1).optional(),
  date: z.string().optional(),
  date_from: z.string().optional(),
  date_to: z.string().optional(),
  camera_id: z.string().optional(),
})

const EventsByHourSchema = z.object({
  kind: z.literal('events_by_hour'),
  site_id: z.string().min(1).optional(),
  date: z.string().min(1),
  camera_id: z.string().optional(),
})

const NavigateSchema = z.object({
  kind: z.literal('navigate'),
  to: z.string().min(1),
  params: z.record(z.string()).optional(),
  search: z.record(z.string()).optional(),
})

// ── Composed view block schemas (CDV-019..CDV-026) ─────────────
//
// Each block is `{ type: <literal>, …params }`. The discriminator is
// `type`, not `kind`, so the catalogue stays unambiguous when a data
// kind appears both at top level (`{kind:"photos",…}`) and as a block
// (`{type:"photos",…}`).

const MarkdownBlock = z.object({
  type: z.literal('markdown'),
  text: z.string().min(1),
})

const TableBlock = z.object({
  type: z.literal('table'),
  columns: z.array(z.string()).min(1),
  rows: z.array(z.array(z.union([z.string(), z.number(), z.null()]))),
  caption: z.string().optional(),
})

const ListItem = z.union([
  z.string(),
  z.object({ text: z.string(), hint: z.string().optional() }),
])
const ListBlock = z.object({
  type: z.literal('list'),
  items: z.array(ListItem).min(1),
  ordered: z.boolean().optional(),
})

const KpiBlock = z.object({
  type: z.literal('kpi'),
  label: z.string().min(1),
  value: z.union([z.string(), z.number()]),
  unit: z.string().optional(),
  trend: z.enum(['up', 'down', 'flat']).optional(),
  caption: z.string().optional(),
})

const FormField = z.object({
  name: z.string().min(1),
  label: z.string().min(1),
  type: z.enum(['text', 'textarea', 'number', 'date', 'select']),
  required: z.boolean().optional(),
  placeholder: z.string().optional(),
  options: z.array(z.object({ value: z.string(), label: z.string() })).optional(),
  default: z.union([z.string(), z.number()]).optional(),
})
const FormBlock = z.object({
  type: z.literal('form'),
  id: z.string().min(1),
  title: z.string().optional(),
  description: z.string().optional(),
  submit_label: z.string().optional(),
  fields: z.array(FormField).min(1),
})

// WP1B PROJ-UPD: a conversational confirmation with a few named action
// buttons — no input fields. Distinct from `form` (which is a data-entry
// widget with exactly one submit action) because a Project Update
// confirmation needs several distinct actions (Save / Edit / Discard,
// or Accept as project status / Keep as reported) with no fields to fill.
const ConfirmCardAction = z.object({
  value: z.string().min(1),
  label: z.string().min(1),
})
const ConfirmCardBlock = z.object({
  type: z.literal('confirm_card'),
  id: z.string().min(1),
  text: z.string().min(1),
  actions: z.array(ConfirmCardAction).min(1),
  payload: z.record(z.unknown()).optional(),
})

// Data blocks: same params as the standalone kind, with `type` swapped
// in for `kind`. We keep one canonical definition per kind by deriving
// the block schema from the kind schema below; this guarantees that any
// future field added to a kind also flows into the embeddable block.
const PhotosBlock = PhotosSchema.omit({ kind: true }).extend({ type: z.literal('photos') })
const AnalysesBlock = AnalysesSchema.omit({ kind: true }).extend({ type: z.literal('analyses') })
const TimelineBlock = TimelineSchema.omit({ kind: true }).extend({ type: z.literal('timeline') })
const EventsByHourBlock = EventsByHourSchema.omit({ kind: true }).extend({
  type: z.literal('events_by_hour'),
  // events_by_hour requires date as a kind, but as an embedded block we
  // let the composed top-level / page_context cascade fill it in. The
  // render-time cascade either resolves it or surfaces the standard
  // "block could not be displayed" note (CDV-025).
  date: z.string().min(1).optional(),
})

export const BlockSchema = z.discriminatedUnion('type', [
  MarkdownBlock,
  TableBlock,
  ListBlock,
  KpiBlock,
  FormBlock,
  ConfirmCardBlock,
  PhotosBlock,
  AnalysesBlock,
  TimelineBlock,
  EventsByHourBlock,
])
export type Block = z.infer<typeof BlockSchema>

const ComposedSchema = z.object({
  kind: z.literal('composed'),
  title: z.string().optional(),
  site_id: z.string().min(1).optional(),
  camera_id: z.string().optional(),
  blocks: z.array(BlockSchema),
})

export const ViewIntentSchema = z.discriminatedUnion('kind', [
  PhotosSchema,
  AnalysesSchema,
  TimelineSchema,
  EventsByHourSchema,
  NavigateSchema,
  ComposedSchema,
])

export type ViewIntent = z.infer<typeof ViewIntentSchema>
export type ComposedIntent = Extract<ViewIntent, { kind: 'composed' }>

export interface ExtractResult {
  clean: string
  intent: ViewIntent | null
  raw: string | null
  error: string | null
}

// Strip every occurrence of the ⟦VIEW⟧…⟦/VIEW⟧ marker from the text; only
// the first one is parsed as an intent. Tolerates newlines inside the JSON
// payload (the regex uses [\s\S]*? to cross line breaks).
export function extractViewIntent(text: string): ExtractResult {
  if (!text) {
    return { clean: text ?? '', intent: null, raw: null, error: null }
  }

  const first = text.match(MARKER_RE)
  if (!first) {
    return { clean: text, intent: null, raw: null, error: null }
  }

  const raw = first[1] ?? ''
  const cleanedAll = text.replace(new RegExp(MARKER_RE.source, 'g'), '').trim()

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch (e) {
    return {
      clean: cleanedAll,
      intent: null,
      raw,
      error: `json parse error: ${(e as Error).message}`,
    }
  }

  const result = ViewIntentSchema.safeParse(parsed)
  if (!result.success) {
    return {
      clean: cleanedAll,
      intent: null,
      raw,
      error: result.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; '),
    }
  }

  return { clean: cleanedAll, intent: result.data, raw, error: null }
}

// page_context shapes carried on every chat request; subset of the
// ChatPageContext union that has scoped ids.
export type ViewPageContext =
  | { scope: 'portfolio' }
  | { scope: 'site'; site_id: string }
  | { scope: 'camera'; site_id: string; camera_id: string }
  | { scope: 'alerts'; site_id: string }

// SalaIntent is what AppShell receives from the URL: every kind that
// activates a sala (i.e. everything except `navigate`). For single-kind
// salas a top-level `site_id` is required (and is filled by the planner
// before encoding); for `composed` salas it is optional, since each
// data block resolves its own scope through the cascade in CDV-027.
export type SalaIntent =
  | (Exclude<ViewIntent, { kind: 'navigate' | 'composed' }> & { site_id: string })
  | ComposedIntent

export type ViewIntentPlan =
  | { type: 'navigate'; to: string; params?: Record<string, string>; search?: Record<string, string> }
  | { type: 'sala'; intent: SalaIntent }
  | { type: 'invalid'; reason: string }

/**
 * Resolve scope defaults against the current page_context and decide what
 * the dispatcher should do:
 *
 *   - `navigate` intents pass through verbatim (no scope fill).
 *   - `composed` intents fill their TOP-LEVEL site_id/camera_id from
 *     page_context if missing. Per-block scope is resolved at render
 *     time by the cascade in `composedScope.ts`.
 *   - every other kind requires `site_id`; fill it from page_context when
 *     the assistant omitted it. `camera_id` is filled only when the
 *     page_context is a camera page AND the intent has no explicit camera.
 *   - if `site_id` is still missing after the fill, the plan is `invalid`
 *     (CDV-014 second Given/When/Then — "portfolio view + photos without
 *     site_id is a text fallback").
 */
export function planViewIntent(intent: ViewIntent, ctx: ViewPageContext): ViewIntentPlan {
  if (intent.kind === 'navigate') {
    return { type: 'navigate', to: intent.to, params: intent.params, search: intent.search }
  }

  const ctxSite = 'site_id' in ctx ? ctx.site_id : undefined
  const ctxCamera = ctx.scope === 'camera' ? ctx.camera_id : undefined

  if (intent.kind === 'composed') {
    // Top-level scope is optional but useful: fill from page_context so
    // embedded blocks have one more cascade source. Composed never goes
    // invalid here — empty blocks renders the empty state (CDV-017),
    // unresolved per-block scope renders the per-block error (CDV-025).
    const filled: ComposedIntent = {
      ...intent,
      ...(intent.site_id ?? ctxSite ? { site_id: intent.site_id ?? ctxSite } : {}),
      ...(intent.camera_id ?? ctxCamera ? { camera_id: intent.camera_id ?? ctxCamera } : {}),
    }
    return { type: 'sala', intent: filled }
  }

  const siteId = intent.site_id ?? ctxSite
  if (!siteId) {
    return { type: 'invalid', reason: 'site_id missing and no site in page_context' }
  }

  const cameraId = intent.camera_id ?? ctxCamera
  const filled = { ...intent, site_id: siteId, ...(cameraId ? { camera_id: cameraId } : {}) }

  // photos kind needs either a date or a curated event_ids list; empty
  // intent would show the whole camera feed ever, which is never useful.
  if (filled.kind === 'photos') {
    const hasIds = Array.isArray(filled.event_ids) && filled.event_ids.length > 0
    if (!filled.date && !hasIds) {
      return { type: 'invalid', reason: 'photos intent needs date or event_ids' }
    }
  }

  return { type: 'sala', intent: filled as SalaIntent }
}

// URL encoding of the `sala` search param. The param value is the full
// SalaIntent serialised as JSON; the URL search machinery of TanStack
// Router handles percent-escaping.
export function encodeSalaParam(intent: SalaIntent): string {
  return JSON.stringify(intent)
}

// Reverse of encodeSalaParam. Returns null (not throws) for every
// malformed input so callers can safely treat "no sala" and "bad sala"
// identically — both just render the underlying route.
//
// `composed` is allowed without a top-level site_id (per-block scope
// cascade resolves it at render time). Single-kind salas still require
// site_id, mirroring what the planner produces.
export function parseSalaParam(raw: unknown): SalaIntent | null {
  if (typeof raw !== 'string' || raw.length === 0) return null
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  const result = ViewIntentSchema.safeParse(parsed)
  if (!result.success) return null
  if (result.data.kind === 'navigate') return null
  if (result.data.kind === 'composed') return result.data
  if (!result.data.site_id) return null
  return result.data as SalaIntent
}
